import type {
  AttackWave,
  FixedDefence,
  MobileDefence,
  ScenarioConfig,
  SimEvent,
  SimOptions,
  SimResult,
  StrikeWeapon,
  Target,
  Vec2,
  World,
} from "./types.js";
import { buildScenario } from "./scenario.js";
import { computeMetrics } from "./metrics.js";
import { makeRng, type RNG } from "./rng.js";

const DEFAULT_OPTIONS: SimOptions = { dt: 0.5, maxTime: 600 };

export function runSim(config: ScenarioConfig, options: Partial<SimOptions> = {}): SimResult {
  const opts: SimOptions = { ...DEFAULT_OPTIONS, ...options };
  const rng = makeRng(config.seed ^ 0xa5a5a5a5);
  const { world, waves } = buildScenario(config);
  const events: SimEvent[] = [];

  emitInitialMobilePositions(world, events);

  let t = 0;
  while (t <= opts.maxTime) {
    launchDueWaves(t, waves, events);
    moveWeapons(t, opts.dt, world.targets, waves);
    runSensors(t, world, waves, rng, events);
    moveMobileDefences(t, opts.dt, world, waves, events);
    runFixedDefences(t, world, waves, rng, events);
    runMobileDefences(t, world, waves, rng, events);
    impactWeapons(t, world.targets, waves, rng, events);

    if (allWeaponsTerminal(waves) && allWavesLaunched(waves, t)) break;
    t = round(t + opts.dt);
  }

  const metrics = computeMetrics(world, waves, events);
  return { config, options: opts, events, metrics, finalWorld: world, waves };
}

function launchDueWaves(t: number, waves: AttackWave[], events: SimEvent[]): void {
  for (const wave of waves) {
    if (wave.launched || wave.startTime > t) continue;
    wave.launched = true;
    for (const w of wave.weapons) {
      w.launchedAt = t;
      events.push({ t, type: "launch", weaponId: w.id, targetId: w.targetId, pos: { ...w.pos } });
    }
  }
}

function moveWeapons(t: number, dt: number, targets: Target[], waves: AttackWave[]): void {
  for (const wave of waves) {
    if (!wave.launched) continue;
    for (const w of wave.weapons) {
      if (w.destroyed || w.terminalAt !== null) continue;
      const target = targets.find((tg) => tg.id === w.targetId);
      if (!target) continue;
      const dest = target.pos;
      const dx = dest.x - w.pos.x;
      const dy = dest.y - w.pos.y;
      const dist = Math.hypot(dx, dy);
      const step = w.speed * dt;
      if (dist <= step) {
        w.pos = { x: dest.x, y: dest.y };
      } else {
        w.pos = { x: w.pos.x + (dx / dist) * step, y: w.pos.y + (dy / dist) * step };
      }
    }
  }
}

function runSensors(
  t: number,
  world: World,
  waves: AttackWave[],
  rng: RNG,
  events: SimEvent[],
): void {
  const allSensors = [
    ...world.sensors.map((s) => ({
      id: s.id,
      pos: s.pos,
      range: s.range,
      pDetect: s.detectionProbability,
    })),
    ...world.mobileDefences.map((m) => ({
      id: m.id,
      pos: m.pos,
      range: m.sensorRange,
      pDetect: 0.9,
    })),
  ];
  for (const wave of waves) {
    if (!wave.launched) continue;
    for (const w of wave.weapons) {
      if (w.detected || w.destroyed || w.terminalAt !== null) continue;
      for (const s of allSensors) {
        const d = Math.hypot(s.pos.x - w.pos.x, s.pos.y - w.pos.y);
        if (d <= s.range && rng() < s.pDetect) {
          w.detected = true;
          w.detectedAt = t;
          events.push({ t, type: "detect", sensorId: s.id, weaponId: w.id });
          break;
        }
      }
    }
  }
}

function runFixedDefences(
  t: number,
  world: World,
  waves: AttackWave[],
  rng: RNG,
  events: SimEvent[],
): void {
  for (const fd of world.fixedDefences) {
    if (t < fd.reloadUntil || fd.magazine <= 0) continue;
    const target = pickInterceptTarget(fd.pos, fd.range, waves, true);
    if (!target) continue;
    fd.magazine -= 1;
    fd.shotsFired += 1;
    fd.reloadUntil = round(t + fd.reloadTime);
    const success = rng() < fd.pKill;
    events.push({
      t,
      type: "intercept",
      defenderId: fd.id,
      weaponId: target.id,
      success,
      pos: { ...target.pos },
      from: { ...fd.pos },
    });
    if (success) {
      target.destroyed = true;
      target.terminalAt = t;
    }
  }
}

function emitInitialMobilePositions(world: World, events: SimEvent[]): void {
  for (const md of world.mobileDefences) {
    events.push({ t: 0, type: "move", unitId: md.id, pos: { ...md.pos } });
  }
}

function moveMobileDefences(
  t: number,
  dt: number,
  world: World,
  waves: AttackWave[],
  events: SimEvent[],
): void {
  for (const md of world.mobileDefences) {
    if (md.magazine <= 0) {
      md.lockedThreatId = null;
      md.cachedScreenPoint = null;
    }
    acquireOrKeepLock(md, world.mobileDefences, waves, world.targets);
    const dest = md.cachedScreenPoint ?? md.patrolPoint;
    const dx = dest.x - md.pos.x;
    const dy = dest.y - md.pos.y;
    const dist = Math.hypot(dx, dy);
    const step = md.speed * dt;
    let moved = false;
    if (dist <= step && dist > 0) {
      md.pos = { x: dest.x, y: dest.y };
      moved = true;
    } else if (dist > 0) {
      md.pos = {
        x: md.pos.x + (dx / dist) * step,
        y: md.pos.y + (dy / dist) * step,
      };
      moved = true;
    }
    if (moved) {
      events.push({ t, type: "move", unitId: md.id, pos: { ...md.pos } });
    }
  }
}

function lockedThreat(md: MobileDefence, waves: AttackWave[]): StrikeWeapon | null {
  if (md.lockedThreatId === null) return null;
  for (const wave of waves) {
    if (!wave.launched) continue;
    for (const w of wave.weapons) {
      if (w.id !== md.lockedThreatId) continue;
      if (w.destroyed || w.terminalAt !== null) {
        md.lockedThreatId = null;
        return null;
      }
      return w;
    }
  }
  md.lockedThreatId = null;
  return null;
}

function acquireOrKeepLock(
  md: MobileDefence,
  allMobiles: MobileDefence[],
  waves: AttackWave[],
  targets: Target[],
): StrikeWeapon | null {
  const existing = lockedThreat(md, waves);
  if (existing) return existing;
  const claimed = new Set<string>();
  for (const other of allMobiles) {
    if (other.id !== md.id && other.lockedThreatId !== null) {
      claimed.add(other.lockedThreatId);
    }
  }
  const best = closestDetectedThreat(md, waves, claimed)
    ?? closestDetectedThreat(md, waves, null);
  if (best) {
    md.lockedThreatId = best.id;
    if (md.cachedScreenPoint === null) {
      md.cachedScreenPoint = screenPositionFromThreat(md, best, targets);
    }
  }
  return best;
}

function closestDetectedThreat(
  md: MobileDefence,
  waves: AttackWave[],
  exclude: Set<string> | null,
): StrikeWeapon | null {
  let best: StrikeWeapon | null = null;
  let bestDist = Infinity;
  for (const wave of waves) {
    if (!wave.launched) continue;
    for (const w of wave.weapons) {
      if (w.destroyed || w.terminalAt !== null || !w.detected) continue;
      if (exclude && exclude.has(w.id)) continue;
      const d = Math.hypot(md.pos.x - w.pos.x, md.pos.y - w.pos.y);
      if (d < bestDist) {
        best = w;
        bestDist = d;
      }
    }
  }
  return best;
}

function screenPositionFromThreat(
  md: MobileDefence,
  threat: StrikeWeapon,
  targets: Target[],
): Vec2 {
  const target = targets.find((tg) => tg.id === threat.targetId);
  if (!target) return { ...threat.pos };
  const dx = threat.pos.x - target.pos.x;
  const dy = threat.pos.y - target.pos.y;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return { ...target.pos };
  const speedRatio = md.speed / (md.speed + threat.speed);
  const minAdvance = md.engagementRange * 0.5;
  const advance = Math.min(dist, Math.max(minAdvance, dist * speedRatio));
  return {
    x: target.pos.x + (dx / dist) * advance,
    y: target.pos.y + (dy / dist) * advance,
  };
}

function runMobileDefences(
  t: number,
  world: World,
  waves: AttackWave[],
  rng: RNG,
  events: SimEvent[],
): void {
  for (const md of world.mobileDefences) {
    if (t < md.reloadUntil || md.magazine <= 0) continue;
    const threat = lockedThreat(md, waves);
    if (!threat || !threat.detected) continue;
    const d = Math.hypot(md.pos.x - threat.pos.x, md.pos.y - threat.pos.y);
    if (d > md.engagementRange) continue;
    md.magazine -= 1;
    md.shotsFired += 1;
    md.reloadUntil = round(t + md.reloadTime);
    const success = rng() < md.pKill;
    events.push({
      t,
      type: "intercept",
      defenderId: md.id,
      weaponId: threat.id,
      success,
      pos: { ...threat.pos },
      from: { ...md.pos },
    });
    if (success) {
      threat.destroyed = true;
      threat.terminalAt = t;
    }
  }
}

function pickInterceptTarget(
  defenderPos: Vec2,
  range: number,
  waves: AttackWave[],
  requireDetected: boolean,
): StrikeWeapon | null {
  let best: StrikeWeapon | null = null;
  let bestDist = Infinity;
  for (const wave of waves) {
    if (!wave.launched) continue;
    for (const w of wave.weapons) {
      if (w.destroyed || w.terminalAt !== null) continue;
      if (requireDetected && !w.detected) continue;
      const d = Math.hypot(defenderPos.x - w.pos.x, defenderPos.y - w.pos.y);
      if (d <= range && d < bestDist) {
        best = w;
        bestDist = d;
      }
    }
  }
  return best;
}

function impactWeapons(
  t: number,
  targets: Target[],
  waves: AttackWave[],
  rng: RNG,
  events: SimEvent[],
): void {
  for (const wave of waves) {
    if (!wave.launched) continue;
    for (const w of wave.weapons) {
      if (w.destroyed || w.terminalAt !== null) continue;
      const target = targets.find((tg) => tg.id === w.targetId);
      if (!target) continue;
      const reached = w.pos.x === target.pos.x && w.pos.y === target.pos.y;
      if (!reached) continue;
      w.terminalAt = t;
      const damage = w.pDamage * (1 - target.hardness);
      events.push({
        t,
        type: "impact",
        weaponId: w.id,
        targetId: target.id,
        damage,
        pos: { ...target.pos },
      });
      if (!target.destroyed && rng() < damage) {
        target.destroyed = true;
        events.push({ t, type: "targetDestroyed", targetId: target.id });
      }
    }
  }
}

function allWeaponsTerminal(waves: AttackWave[]): boolean {
  for (const wave of waves) {
    if (!wave.launched) return false;
    for (const w of wave.weapons) {
      if (!w.destroyed && w.terminalAt === null) return false;
    }
  }
  return true;
}

function allWavesLaunched(waves: AttackWave[], t: number): boolean {
  return waves.every((w) => w.launched || w.startTime <= t);
}

function round(t: number): number {
  return Math.round(t * 1000) / 1000;
}
